import { useQuery } from '@tanstack/react-query';
import { api, type Attendance, type Stream, type ViewerSeries } from '../api';
import { Tip, when } from '../ui';

/**
 * Viewers (claude/viewers-plan.md). Live: an estimate from the hosted player,
 * exact up to ~400 viewers. Afterwards: counted from the CDN logs, every
 * player included, about an hour later.
 */
export function ViewersCard({ stream, now }: { stream: Stream; now: number }) {
  const v = stream.viewers;
  const live = stream.status === 'live' || stream.status === 'pending';
  const series = useQuery({
    queryKey: ['viewers', stream.id],
    queryFn: () => api<ViewerSeries>(`/v1/streams/${encodeURIComponent(stream.id)}/viewers`),
    refetchInterval: live ? 60_000 : false,
    enabled: !!stream.went_live_at,
  });
  if (!v) return null;
  const n = v.now;
  const approx = n && !n.exact ? '≈ ' : '';
  const points = series.data ? (series.data.cdn.length ? series.data.cdn.map((p) => ({ t: p.minute, y: p.viewers })) : series.data.player.map((p) => ({ t: p.minute, y: p.watching }))) : [];
  const fromCdn = !!series.data?.cdn.length;

  return (
    <section className="card" aria-labelledby="h-viewers">
      <div className="card-head">
        <h2 id="h-viewers">
          Viewers
          <Tip>
            While the stream runs, counted from the hosted player: exact up to about 400 viewers, within about 5% above that. Your own player isn't included
            live. After the stream, the CDN's logs give the exact count for every player, about an hour later.
          </Tip>
        </h2>
        {n && (
          <span className="row" style={{ gap: 6, fontSize: 13, color: 'var(--accent)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
            {approx}
            {n.watching.toLocaleString()} watching now
          </span>
        )}
      </div>
      <div className="grid-4">
        {live ? (
          <>
            <div className="stat">
              <span className="k">Watching</span>
              <span className="v">{n ? `${approx}${n.watching.toLocaleString()}` : '0'}</span>
              <span className="n">{n ? `measured ${when(n.measured_at, now)}` : 'nobody on the player yet'}</span>
            </div>
            <div className="stat">
              <span className="k">Waiting</span>
              <span className="v">{n ? `${approx}${n.waiting.toLocaleString()}` : '0'}</span>
              <span className="n">on the waiting screen</span>
            </div>
            <div className="stat">
              <span className="k">
                Buffering
                <Tip>Share of viewers whose video is stalled right now. Above a few percent, lower the bitrate or check the upload.</Tip>
              </span>
              <span className="v" style={n && n.buffering_pct >= 5 ? { color: 'var(--warn)' } : undefined}>{n ? `${n.buffering_pct}%` : '—'}</span>
              <span className="n">of those watching</span>
            </div>
          </>
        ) : (
          <>
            <div className="stat">
              <span className="k">Watch time</span>
              <span className="v">{v.watch_minutes != null ? duration(v.watch_minutes) : '—'}</span>
              <span className="n">all viewers together</span>
            </div>
            <div className="stat">
              <span className="k">Average</span>
              <span className="v">{avg(stream, v.watch_minutes)}</span>
              <span className="n">watching at once</span>
            </div>
            <div className="stat">
              <span className="k">Counted from</span>
              <span className="v" style={{ fontFamily: 'inherit', fontSize: 14 }}>{v.source === 'cdn' ? 'CDN logs' : v.source === 'player' ? 'Hosted player' : '—'}</span>
              <span className="n">
                {v.source === 'cdn' ? 'every player, exact' : v.source === 'player' ? 'exact count within ~1 h' : v.cdn_counted_until ? `logs read to ${when(v.cdn_counted_until, now)}` : ''}
              </span>
            </div>
          </>
        )}
        <div className="stat">
          <span className="k">Peak</span>
          <span className="v">{v.peak != null ? v.peak.toLocaleString() : '—'}</span>
          <span className="n">{v.peak_at ? `at ${new Date(v.peak_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'per minute'}</span>
        </div>
      </div>
      {points.length > 1 && <ViewerChart points={points} cdn={fromCdn} />}
      {!!series.data?.countries?.length && <Countries rows={series.data.countries} />}
    </section>
  );
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    return null;
  }
})();
const countryName = (cc: string) => {
  if (!/^[A-Z]{2}$/.test(cc)) return 'Unknown';
  try {
    return regionNames?.of(cc) ?? cc;
  } catch {
    return cc;
  }
};

/** Top countries by watch time, from the CDN logs (every player). */
function Countries({ rows }: { rows: NonNullable<ViewerSeries['countries']> }) {
  const top = rows.slice(0, 8);
  const max = Math.max(...top.map((r) => r.share_pct), 1);
  return (
    <div className="countries" aria-label="Viewers by country">
      <span className="faint" style={{ fontSize: 12 }}>
        Where they watched from
        <Tip>From the CDN logs: share of all watch time per country, every player included. Updated hourly.</Tip>
      </span>
      {top.map((r) => (
        <div key={r.country} className="c">
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{countryName(r.country)}</span>
          <span className="bar">
            <i style={{ width: `${(r.share_pct / max) * 100}%` }} />
          </span>
          <span className="n">
            {r.share_pct}% · {duration(r.watch_minutes)}
          </span>
        </div>
      ))}
    </div>
  );
}

function avg(s: Stream, watchMinutes: number | null): string {
  if (watchMinutes == null || !s.went_live_at) return '—';
  const end = s.ended_at ? Date.parse(s.ended_at) : Date.now();
  const liveMin = (end - Date.parse(s.went_live_at)) / 60_000;
  return liveMin >= 1 ? Math.round(watchMinutes / liveMin).toLocaleString() : '—';
}

function duration(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  const h = min / 60;
  return h < 100 ? `${h.toFixed(1)} h` : `${Math.round(h).toLocaleString()} h`;
}

function ViewerChart({ points, cdn }: { points: { t: string; y: number }[]; cdn: boolean }) {
  const W = 600;
  const H = 80;
  const top = 12;
  const max = Math.max(1, ...points.map((p) => p.y));
  const t0 = Date.parse(points[0].t);
  const t1 = Date.parse(points[points.length - 1].t);
  const x = (t: string) => ((Date.parse(t) - t0) / Math.max(1, t1 - t0)) * W;
  const y = (v: number) => H - (v / max) * (H - top);
  const line = points.map((p) => `${x(p.t).toFixed(1)},${y(p.y).toFixed(1)}`).join(' ');
  const area = `0,${H} ${line} ${W},${H}`;
  const color = cdn ? '#afd136' : '#7fa3df';
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Viewers per minute, peak ${Math.round(max)}`}>
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      <text x={4} y={top} fill="#9aa8c0" fontSize="10" fontFamily="JetBrains Mono, monospace">
        {Math.round(max).toLocaleString()} {cdn ? 'viewers · CDN' : 'viewers · player'}
      </text>
      <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#2a3a5c" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * Who watched, for viewers your site signed in with a viewer token (docs:
 * /viewer-tokens#attendance). Shown once there is anyone on the list; a CSV
 * of everyone, page by page.
 */
export function AttendanceCard({ stream, now }: { stream: Stream; now: number }) {
  const live = stream.status === 'live' || stream.status === 'pending';
  const q = useQuery({
    queryKey: ['attendance', stream.id],
    queryFn: () => api<{ data: Attendance[]; total: number; has_more: boolean }>(`/v1/streams/${encodeURIComponent(stream.id)}/attendance?limit=1000`),
    refetchInterval: live ? 60_000 : false,
  });
  if (!q.data || q.data.total === 0) return null;
  const rows = [...q.data.data].sort((a, b) => b.watch_seconds - a.watch_seconds);
  const mins = (s: number) => (s < 60 ? `${s} s` : `${Math.round(s / 60)} min`);
  const download = async () => {
    const all: Attendance[] = [];
    let after: string | null = null;
    do {
      const page: { data: Attendance[]; next_after: string | null } = await api(
        `/v1/streams/${encodeURIComponent(stream.id)}/attendance?limit=1000${after ? `&after=${encodeURIComponent(after)}` : ''}`,
      );
      all.push(...page.data);
      after = page.next_after;
    } while (after);
    const cell = (v: string | number | null) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = ['viewer_id,name,watch_seconds,first_seen_at,last_seen_at']
      .concat(all.map((r) => [r.viewer_id, r.name, r.watch_seconds, r.first_seen_at, r.last_seen_at].map(cell).join(',')))
      .join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `${stream.id}-attendance.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <section className="card" aria-labelledby="h-attendance">
      <div className="card-head">
        <h2 id="h-attendance">
          Attendance
          <Tip>
            Viewers your site signed in with a viewer token, and how long each one watched in the hosted player (paused time not counted). Up to a minute behind
            while live. Anonymous viewers are only counted, above.
          </Tip>
        </h2>
        <button type="button" className="btn small" onClick={() => void download()}>
          Download CSV
        </button>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        {q.data.total.toLocaleString()} signed-in {q.data.total === 1 ? 'viewer' : 'viewers'}
      </p>
      <div className="table attendance" role="table" aria-label="Attendance">
        <div className="tr th" role="row">
          <span role="columnheader">Name</span>
          <span role="columnheader">Your id</span>
          <span role="columnheader">Watched</span>
          <span role="columnheader">Last seen</span>
        </div>
        {rows.slice(0, 50).map((r) => (
          <div key={r.viewer_id} className="tr" role="row">
            <span role="cell" style={{ fontWeight: 500 }}>{r.name ?? '—'}</span>
            <span role="cell" className="cell-mono">{r.viewer_id}</span>
            <span role="cell">{mins(r.watch_seconds)}</span>
            <span role="cell" className="muted">{when(r.last_seen_at, now)}</span>
          </div>
        ))}
      </div>
      {q.data.total > 50 && <p className="muted" style={{ margin: 0 }}>The 50 who watched longest; the CSV has everyone.</p>}
    </section>
  );
}
